import React from 'react';

const baseData = [
  {
    label: '2 x $550K Properties',
    price: 550000 * 2,
    depositPercent: 20,
    loanAmount: 440000 * 2,
    monthlyRepayment: 2638 * 2,
    rentalYield: 5,
    annualRent: 27500 * 2,
    netRent: 19250 * 2,
    annualShortfall: -12406 * 2,
    tenYearGrowth: 1080000 * 2,
    netGain: 956000 * 2
  },
  {
    label: '1 x $700K Property',
    price: 700000,
    depositPercent: 20,
    loanAmount: 560000,
    monthlyRepayment: 3361,
    rentalYield: 5,
    annualRent: 35000,
    netRent: 24500,
    annualShortfall: -15832,
    tenYearGrowth: 1380000,
    netGain: 1220000
  }
];

const StrategyComparison = () => {
  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">📘 Ayaon & Akansha's Investment Strategy</h2>

      <p className="mb-4 text-sm text-gray-700">
        Investor 1 (38) earns $202,000 and pays $57,000 in tax. Investor 2 (37) earns $178,000 and pays $47,000 in tax. Together, their combined income is $380,000 with $104,000 in annual tax paid. They have $600,000 in savings, $50,000 in shares, and $220,000 in super, but also a $1.05M home loan and $23,000 in credit card debt, putting their net worth at -$203,000.
        <br/><br/>
        Their goal is financial independence by age 60 — 22 years away. To fund a 30-year retirement at $3,000/week, they need a future portfolio worth ~$11.7M. With a current shortfall of over $11.9M, they must grow their investments rapidly, averaging ~$540K/year in net portfolio gains.
      </p>

      <h3 className="text-lg font-semibold mb-2">💼 Property Investment Options</h3>
      <table className="min-w-full border text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 border">Scenario</th>
            <th className="p-2 border">Total Price</th>
            <th className="p-2 border">Deposit</th>
            <th className="p-2 border">Loan</th>
            <th className="p-2 border">Monthly Repayment</th>
            <th className="p-2 border">Annual Rent</th>
            <th className="p-2 border">Net Rent (70%)</th>
            <th className="p-2 border">Annual Shortfall</th>
            <th className="p-2 border">10-Yr Value</th>
            <th className="p-2 border font-semibold">Net Gain (10 yrs)</th>
          </tr>
        </thead>
        <tbody>
          {baseData.map((item, i) => (
            <tr key={i} className="border-b">
              <td className="p-2 border font-medium">{item.label}</td>
              <td className="p-2 border">${item.price.toLocaleString()}</td>
              <td className="p-2 border">${((item.price * item.depositPercent) / 100).toLocaleString()}</td>
              <td className="p-2 border">${item.loanAmount.toLocaleString()}</td>
              <td className="p-2 border">${item.monthlyRepayment.toLocaleString()}</td>
              <td className="p-2 border">${item.annualRent.toLocaleString()}</td>
              <td className="p-2 border">${item.netRent.toLocaleString()}</td>
              <td className="p-2 border">${item.annualShortfall.toLocaleString()}</td>
              <td className="p-2 border">${item.tenYearGrowth.toLocaleString()}</td>
              <td className="p-2 border font-semibold text-green-700">${item.netGain.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 text-sm text-gray-700">
        <p>💡 <strong>Recommendation for Ayaon & Akansha:</strong></p>
        <ul className="list-disc ml-5">
          <li>Buying <strong>2 × $550K properties</strong> provides a higher total return ($1.91M value, $1.17M net gain) vs 1 × $700K property.</li>
          <li>This creates diversification across suburbs and asset types, reducing risk.</li>
          <li>Smaller loans mean more manageable cashflow and greater resilience to interest rate hikes.</li>
          <li>Strong cashflow supports saving for the next investment property, helping reach their goals faster before age 60.</li>
          <li>Overall, this strategy is better suited for compounding wealth and scaling the portfolio over the next two decades.</li>
        </ul>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-2">📈 Equity Unlock Simulation (After 1 Year)</h3>
        <p className="text-sm mb-4 text-gray-700">
          Assuming a 10% growth in property value over the next 12 months, here’s how much equity Ayaon & Akansha could unlock and what additional purchase power that gives them:
        </p>
        <table className="min-w-full border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">Scenario</th>
              <th className="p-2 border">New Value</th>
              <th className="p-2 border">Current Loan</th>
              <th className="p-2 border">New Equity</th>
              <th className="p-2 border">Usable Equity (80%)</th>
              <th className="p-2 border">Buy Power (20% deposit)</th>
            </tr>
          </thead>
          <tbody>
            {baseData.map((item, i) => {
              const newValue = item.price * 1.1;
              const newEquity = newValue - item.loanAmount;
              const usableEquity = newEquity * 0.8;
              const buyPower = usableEquity / 0.2;
              return (
                <tr key={i} className="border-b">
                  <td className="p-2 border font-medium">{item.label}</td>
                  <td className="p-2 border">${newValue.toLocaleString()}</td>
                  <td className="p-2 border">${item.loanAmount.toLocaleString()}</td>
                  <td className="p-2 border">${newEquity.toLocaleString()}</td>
                  <td className="p-2 border">${usableEquity.toLocaleString()}</td>
                  <td className="p-2 border font-semibold text-blue-700">${buyPower.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-4 text-sm text-gray-700">
          🎯 This means after just one year of disciplined investment, Ayaon & Akansha could have the equity buffer to acquire another property worth up to <strong>$500K–$600K</strong> — accelerating their wealth creation journey.
        </p>
      </div>
    </div>
  );
};

export default StrategyComparison;
